import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { therapistsApi, therapistsAvailability, sessionsApi } from '@/api/endpoints';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { NfseEligibilityBadge } from '@/components/shared/nfse-eligibility-badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/terapeutas/$id/agendar')({
  validateSearch: (s: Record<string, unknown>) => ({
    slot: typeof s.slot === 'string' ? s.slot : undefined,
    modality: typeof s.modality === 'string' ? s.modality : undefined,
  }),
  component: AgendarPage,
});

const DOW = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const TZ = 'UTC';

function startOfWeek(date: Date): Date {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}
function weekLabel(d: Date) {
  const end = new Date(d.getTime() + 6 * 86400000);
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: TZ })} – ${end.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', timeZone: TZ })}`;
}

function AgendarPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch() as { slot?: string; modality?: string };
  const navigate = useNavigate();

  // Slot escolhido — vem pré-selecionado se o cliente já clicou num horário
  // do calendário inline na lista /terapeutas (search.slot é o ISO do slot).
  const [selectedSlot, setSelectedSlot] = useState<string | null>(search.slot ?? null);
  const [weekStart, setWeekStart] = useState(() => {
    if (search.slot) return startOfWeek(new Date(search.slot));
    return startOfWeek(new Date());
  });
  const [modality, setModality] = useState<'ONLINE' | 'PRESENCIAL'>(
    (search.modality === 'PRESENCIAL' ? 'PRESENCIAL' : 'ONLINE'),
  );

  useEffect(() => {
    if (search.slot) {
      setSelectedSlot(search.slot);
      setWeekStart(startOfWeek(new Date(search.slot)));
    }
  }, [search.slot]);

  const { data: therapist, isLoading } = useQuery({
    queryKey: ['therapist', id],
    queryFn: () => therapistsApi.get(id),
  });

  // FB-6/10 — antes usávamos <input type=date>+<input type=time> livres,
  // permitindo o cliente escolher qualquer horário e ignorando a agenda do
  // profissional. Agora consulta a mesma computeAvailability usada em
  // RescheduleModal / wizard de recorrência.
  const availabilityQ = useQuery({
    queryKey: ['availability', id, weekStart.toISOString()],
    queryFn: () => therapistsAvailability.get(id, weekStart.toISOString()),
    enabled: !!id,
    staleTime: 60_000,
  });

  // Modalidades disponíveis seguem o que o terapeuta cadastrou no perfil:
  // - ONLINE: só online       - PRESENCIAL: só presencial       - HYBRID: ambos
  const therapistModality = therapist?.modality as 'ONLINE' | 'PRESENCIAL' | 'HYBRID' | undefined;
  const allowedModalities: ('ONLINE' | 'PRESENCIAL')[] =
    therapistModality === 'ONLINE' ? ['ONLINE']
      : therapistModality === 'PRESENCIAL' ? ['PRESENCIAL']
      : ['ONLINE', 'PRESENCIAL'];

  // Se o terapeuta só atende uma modalidade, força a seleção
  useEffect(() => {
    if (allowedModalities.length === 1 && modality !== allowedModalities[0]) {
      setModality(allowedModalities[0]);
    }
  }, [allowedModalities.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const book = useMutation({
    mutationFn: sessionsApi.book,
    onSuccess: (s: any) => {
      // Após criar sessão (já com Payment vinculado), envia direto pra página de pagamento
      const paymentId = s.payment?.id;
      if (paymentId) {
        navigate({ to: '/pagamento/$paymentId', params: { paymentId } });
      } else {
        navigate({ to: '/dashboard/sessoes/$id', params: { id: s.id } });
      }
    },
  });

  // Não logado → mostra opção entrar/cadastrar preservando seleção
  if (!tokenStore.getAccess()) {
    const slotQs = search.slot ? `?slot=${encodeURIComponent(search.slot)}&modality=${modality}` : '';
    const next = `/terapeutas/${id}/agendar${slotQs}`;
    return (
      <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-1 max-w-md mx-auto px-6 py-16 w-full">
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <h1 className="text-xl font-bold text-slate-700 text-center">Falta pouco!</h1>
            <p className="text-slate-500 mt-2 mb-6 text-center text-sm">
              Crie uma conta ou entre para confirmar seu agendamento. Salvamos sua escolha
              {search.slot && (
                <> de <strong className="text-slate-700">
                  {new Date(search.slot).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                </strong></>
              )}
              .
            </p>
            <div className="space-y-3">
              <Link
                to="/register"
                search={{ next } as any}
                className="block w-full bg-coral-400 hover:bg-coral-500 text-white text-center px-6 py-3 rounded-full font-medium transition shadow"
              >
                Criar conta →
              </Link>
              <Link
                to="/login"
                search={{ next } as any}
                className="block w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-center px-6 py-3 rounded-full font-medium transition"
              >
                Já tenho conta · Entrar
              </Link>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-2xl mx-auto px-6 py-12 w-full">
        {isLoading || !therapist ? (
          <div className="bg-white rounded-3xl p-12 animate-pulse h-64" />
        ) : (
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              {therapist.avatarUrl || therapist.user?.image ? (
                <img src={therapist.avatarUrl ?? therapist.user.image} alt="" className="size-14 rounded-full object-cover" />
              ) : (
                <div className="size-14 rounded-full bg-gradient-to-br from-teal-300 to-teal-500" />
              )}
              <div>
                <p className="text-xs text-slate-400">Agendando com</p>
                <p className="font-bold text-slate-700">{therapist.user?.name}</p>
              </div>
            </div>

            <h1 className="text-2xl font-extrabold text-slate-800">Confirme seu horário</h1>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!selectedSlot) return;
                book.mutate({ therapistId: id, scheduledAt: selectedSlot, modality });
              }}
              className="mt-6 space-y-4"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Horário</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Apenas horários livres na agenda do profissional</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const d = new Date(weekStart);
                      d.setUTCDate(d.getUTCDate() - 7);
                      if (d.getTime() < Date.now() - 24 * 3600000) return;
                      setWeekStart(d); setSelectedSlot(null);
                    }}>←</Button>
                    <span className="text-xs text-slate-500 font-medium min-w-[110px] text-center">{weekLabel(weekStart)}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const d = new Date(weekStart);
                      d.setUTCDate(d.getUTCDate() + 7);
                      setWeekStart(d); setSelectedSlot(null);
                    }}>→</Button>
                  </div>
                </div>

                {availabilityQ.isLoading && (
                  <div className="flex items-center justify-center py-12 text-sm text-slate-400">Carregando horários…</div>
                )}
                {availabilityQ.data && (
                  <div className="grid grid-cols-7 gap-1.5">
                    {availabilityQ.data.days.map((day) => {
                      const d = new Date(day.date);
                      const todayUtc = (() => { const t = new Date(); return new Date(Date.UTC(t.getFullYear(), t.getMonth(), t.getDate())); })();
                      const isPast = d.getTime() < todayUtc.getTime();
                      const isToday = d.getTime() === todayUtc.getTime();
                      return (
                        <div key={day.date} className="flex flex-col gap-1.5">
                          <div className={cn('text-center pb-2 border-b', isToday ? 'border-teal-300' : 'border-slate-100')}>
                            <div className={cn('text-[10px] font-bold uppercase tracking-wide', isToday ? 'text-teal-500' : 'text-slate-400')}>{DOW[d.getUTCDay()]}</div>
                            <div className={cn('text-xl font-bold leading-tight mt-0.5', isPast ? 'text-slate-300' : isToday ? 'text-teal-600' : 'text-slate-800')}>{d.getUTCDate()}</div>
                          </div>
                          {day.slots.length === 0 ? (
                            <div className="text-[10px] text-slate-200 text-center py-3 select-none">—</div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {day.slots.map((slot) => {
                                const time = new Date(slot).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
                                const isSelected = selectedSlot === slot;
                                return (
                                  <button
                                    type="button"
                                    key={slot}
                                    onClick={() => setSelectedSlot(isSelected ? null : slot)}
                                    className={cn(
                                      'text-[11px] py-1.5 rounded-lg border font-semibold transition-all',
                                      isSelected
                                        ? 'bg-teal-500 text-white border-teal-500 shadow-sm scale-[1.02]'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-600 hover:bg-teal-50',
                                    )}
                                  >
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {availabilityQ.data && availabilityQ.data.days.every((d) => d.slots.length === 0) && (
                  <p className="text-xs text-slate-400 text-center mt-3">Nenhum horário livre nesta semana. Tente a próxima.</p>
                )}
              </div>

              <Field label="Modalidade">
                {allowedModalities.length > 1 ? (
                  <select value={modality} onChange={(e) => setModality(e.target.value as any)} className="w-full bg-white border border-slate-200 rounded-xl h-11 px-3 focus:outline-none focus:ring-2 focus:ring-teal-300">
                    <option value="ONLINE">Online (Google Meet)</option>
                    <option value="PRESENCIAL">Presencial</option>
                  </select>
                ) : (
                  <div className="w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-3 flex items-center text-slate-700">
                    {allowedModalities[0] === 'ONLINE' ? 'Online (Google Meet)' : 'Presencial'}
                  </div>
                )}
              </Field>

              <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between">
                <span className="text-sm text-slate-600">Valor da sessão</span>
                <span className="text-xl font-extrabold text-teal-600">R$ {Number(therapist.sessionPrice).toFixed(2)}</span>
              </div>

              <NfseEligibilityBadge professionalCategory={therapist.professionalCategory} />

              {book.isError && (
                <p className="text-sm text-red-500">Algo deu errado. Verifique os horários e tente novamente.</p>
              )}

              <button
                type="submit"
                disabled={book.isPending || !selectedSlot}
                className="w-full bg-coral-400 hover:bg-coral-500 text-white py-3.5 rounded-full font-bold transition disabled:opacity-50 shadow"
              >
                {book.isPending ? 'Agendando...' : selectedSlot ? 'Confirmar agendamento →' : 'Escolha um horário'}
              </button>
              <p className="text-xs text-slate-400 text-center">
                Você será redirecionado para a página de pagamento (PIX ou cartão).
              </p>
            </form>
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
